import { useCallback, useMemo } from "react";
import axios from "axios";

const { VITE_API_ENDPOINT, VITE_API_KEY } = import.meta.env;

type iApiService = {
  apiGetUserConfiguration: (pin: string) => Promise<any>;
  apiAddUserConfiguration: (payload: any) => Promise<any>;
  apiUpdateUserConfiguration: (pin: string, payload: any) => Promise<any>;
  apiGetVehicleStock: (stockDealerId: string, materialCode: string) => Promise<any>;
  apiGetDealershipInfo: (sapCode: string) => Promise<any>;
  apiGetPricingInfo: (payload: any) => Promise<any>;
  apiGetDealersByLocation: (
    location: string,
    postCode: string,
    config?: any,
  ) => Promise<any>;
  apiGetDealersSuggestions: (searchKey: string, config?: any) => Promise<any>;
  apiGetDealersByURLSubdomain: (subdomain: string) => Promise<any>;
};

export const useApiService = (): iApiService => {
  const axiosWrapper = useMemo(
    () =>
      axios.create({
        baseURL: VITE_API_ENDPOINT,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-api-key": `${VITE_API_KEY}`,
        },
        timeout: 5000,
      }),
    [],
  );

  // ********************
  // Get user config pin data
  // ********************
  const apiGetUserConfiguration = useCallback(
    async (pin: string): Promise<any> =>
      axiosWrapper
        .get(`/api/v1/ebrochure/user-config/${pin}`)
        .then((res: any) => res?.data)
        .catch((err: any) => console.error(err)),
    [axiosWrapper],
  );

  // ********************
  // Create user config pin data
  // ********************
  const apiAddUserConfiguration = useCallback(
    async (payload: any): Promise<any> =>
      axiosWrapper.post("/api/v1/ebrochure/user-config", payload),
    [axiosWrapper],
  );

  // ********************
  // Update user config pin data (configuration or sap code)
  // ********************
  const apiUpdateUserConfiguration = useCallback(
    async (pin: string, payload: any): Promise<any> =>
      axiosWrapper.put(`/api/v1/ebrochure/user-config/${pin}`, payload),
    [axiosWrapper],
  );

  // ********************
  // Get vehicle cash pricing
  // ********************
  const apiGetVehicleStock = useCallback(
    async (stockDealerId: string, materialCode: string): Promise<any> =>
      axiosWrapper
        .get(`/api/v1/ebrochure/stock/${stockDealerId}/${materialCode}`)
        .then((res: any) => res?.data)
        .catch((err: any) => console.error(err)),
    [axiosWrapper],
  );

  // ********************
  // Get dealership data
  // ********************
  const apiGetDealershipInfo = useCallback(
    async (sapCode: string): Promise<any> =>
      axiosWrapper
        .get(`/api/v1/dealership/sap-code/${sapCode}`)
        .then((res: any) => res?.data)
        .catch((err: any) => console.error(err)),
    [axiosWrapper],
  );

  // ********************
  // Get vehicle FSL pricing
  // ********************
  const apiGetPricingInfo = useCallback(
    async (payload: any): Promise<any> =>
      axiosWrapper
        .post(`/api/v1/ebrochure/fsl`, payload)
        .then((res: any) => res?.data)
        .catch((err: any) => console.error(err)),
    [axiosWrapper],
  );

  // ********************
  // Get dealership by location
  // ********************
  const apiGetDealersByLocation = useCallback(
    async (postCode: string, suburb: string, config?: any): Promise<any> =>
      axiosWrapper
        .get(`/api/v2/dealerships/location/${postCode}/${suburb}`, config)
        .then((res: any) => res?.data)
        .catch((err: any) => console.error(err)),
    [axiosWrapper],
  );

  // ********************
  // Get dealership suggestions
  // ********************
  const apiGetDealersSuggestions = useCallback(
    async (searchKey: string, config?: any): Promise<any> =>
      axiosWrapper
        .get(`/api/v2/suburbs/${searchKey}`, config)
        .then((res: any) => res?.data)
        .catch((err: any) => console.error(err)),
    [axiosWrapper],
  );

  // ********************
  // Get dealerships by URL
  // ********************
  const apiGetDealersByURLSubdomain = useCallback(
    async (subdomain: string): Promise<any> =>
      axiosWrapper
        .get(`/api/v1/dealerships/url/${subdomain}`)
        .then((res: any) => res?.data)
        .catch((err: any) => console.error(err)),
    [axiosWrapper],
  );

  return {
    apiGetUserConfiguration,
    apiAddUserConfiguration,
    apiUpdateUserConfiguration,
    apiGetVehicleStock,
    apiGetDealershipInfo,
    apiGetPricingInfo,
    apiGetDealersByLocation,
    apiGetDealersSuggestions,
    apiGetDealersByURLSubdomain,
  };
};
